import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { toast } from "sonner";

/**
 * Handles the redirect back from Emergent OAuth.
 * URL pattern: /auth/callback#session_id=<token>
 * Exchanges session_id with the backend, then navigates to /account.
 */
export default function AuthCallback() {
  const { exchangeGoogleSession } = useConsumerAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setErr("Missing session id");
      return;
    }
    const sid = decodeURIComponent(m[1]);

    (async () => {
      try {
        const user = await exchangeGoogleSession(sid);
        toast.success(`Welcome${user?.username ? `, @${user.username}` : ""}!`);
        // strip hash from URL, then route to /account (or wherever they came from)
        window.history.replaceState(null, "", "/account");
        navigate("/account", { replace: true, state: { user } });
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || "Sign-in failed");
      }
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground"
      data-testid="auth-callback"
    >
      {err ? (
        <>
          <div className="text-rose-300">{err}</div>
          <button
            className="pill"
            onClick={() => navigate("/account", { replace: true })}
            data-testid="auth-callback-retry"
          >
            Back to sign in
          </button>
        </>
      ) : (
        <>
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Finishing sign-in…</span>
        </>
      )}
    </div>
  );
}
